
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, type News } from '../../../lib/supabase';

const NewsSection = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(4);

      if (error) {
        console.error('Error fetching news:', error);
        setNews([]);
        return;
      }

      setNews(data || []);
    } catch (error) {
      console.error('Error:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAll = () => {
    navigate('/news');
  };

  const handleNewsClick = (newsItem: News) => {
    navigate(`/news/${newsItem.id}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <section className="px-4 py-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white">Últimas Noticias</h3>
        <button 
          onClick={handleViewAll}
          className="text-sm font-medium text-pink-400 hover:text-pink-300 transition-colors flex items-center gap-1"
        >
          Ver todas
          <i className="ri-arrow-right-line"></i>
        </button>
      </div>
      
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-3 border-white/20 border-t-pink-500 rounded-full animate-spin"></div>
        </div>
      ) : news.length === 0 ? (
        <div className="text-center py-12 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
          <i className="ri-newspaper-line text-5xl text-white/30 mb-3"></i>
          <p className="text-sm text-white/60">No hay noticias disponibles</p>
        </div>
      ) : (
        <div className="space-y-3">
          {news.map((item) => (
            <div 
              key={item.id} 
              onClick={() => handleNewsClick(item)}
              className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 overflow-hidden hover:bg-white/15 transition-all duration-300 hover:scale-[1.02] cursor-pointer"
            >
              <div className="flex gap-3 p-3">
                {item.image ? (
                  <div className="w-16 h-16 flex-shrink-0 bg-white/5 rounded-xl overflow-hidden">
                    <img 
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                ) : item.video_url ? (
                  <div className="w-16 h-16 flex-shrink-0 bg-black/40 rounded-xl flex items-center justify-center">
                    <i className="ri-play-circle-line text-2xl text-pink-400"></i>
                  </div>
                ) : (
                  <div className="w-16 h-16 flex-shrink-0 bg-white/5 rounded-xl" />
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h4 className="font-semibold text-white text-sm line-clamp-2 flex-1">
                      {item.title}
                    </h4>
                  </div>
                  <p className="text-xs text-white/70 mb-2 line-clamp-2">
                    {item.text}
                  </p>
                  {item.video_url && (
                    <div className="flex items-center text-xs text-pink-300 gap-1 mb-1">
                      <i className="ri-video-line"></i>
                      <span>Video disponible</span>
                    </div>
                  )}
                  <div className="flex items-center text-xs text-white/50">
                    <i className="ri-time-line mr-1"></i>
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <i className="ri-arrow-right-s-line text-white/40 text-lg"></i>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default NewsSection;
